/**
 * Custom metadata provider that supplies pixel spacing from API-provided displayMetadata.
 *
 * Cornerstone's DICOM image loader extracts metadata from DICOM headers automatically,
 * but some veterinary DICOM files store pixel spacing in non-standard tags
 * (e.g. ImagerPixelSpacing instead of PixelSpacing). This provider fills the gap
 * using pre-extracted metadata from the backend.
 */
import { utilities as csCoreUtils, metaData } from '@cornerstonejs/core';
import { wadouri } from '@cornerstonejs/dicom-image-loader';
import { isDataWindowedModality, isUsableStoredWindow } from './autoWindow';
const metadataCache = new Map();
/**
 * Per-imageId cache of extracted ultrasound regions so we don't re-read
 * the DICOM dataset on every `metaData.get('instance', …)` call inside
 * cornerstone's LengthTool stats loop.
 */
const ultrasoundRegionsCache = new Map();
/**
 * Read `SequenceOfUltrasoundRegions` (0018,6011) directly from the
 * wadouri data-set cache. Cornerstone's built-in wadouri metadata
 * provider doesn't surface this tag, which breaks calibrated echo
 * measurement (the raw DICOM has cm/cm regions for B-mode, cm-sec/cm/s
 * regions for Doppler, etc.). We walk the dataset's element tree
 * ourselves and cache the result keyed by imageId.
 *
 * Returns null when there's nothing to read. The dataset cache is
 * loaded lazily by cornerstone; the first call for a freshly loaded
 * image may miss, in which case the caller falls back to px and the
 * next capture will have the data.
 */
/**
 * Resolve an imageId to the dicom-parser DataSet already sitting in the
 * wadouri data-set cache. The cache is keyed by URL with any `frame=` suffix
 * stripped (mirroring the loader's own parseImageId), so multi-frame
 * imageIds resolve to their shared dataset. Returns null when the dataset
 * isn't loaded yet — callers fall back and retry on a later lookup.
 */
export function getWadouriDataSet(imageId) {
    if (!imageId.startsWith('wadouri:'))
        return null;
    let url = imageId.slice('wadouri:'.length);
    // parseImageId cuts the URL one char before 'frame=' (the ? or & separator).
    const frameIndex = url.indexOf('frame=');
    if (frameIndex !== -1)
        url = url.substring(0, frameIndex - 1);
    // wadouri.dataSetCacheManager.get(url) returns the dicom-parser `DataSet`
    // directly (fields: byteArrayParser, byteArray, elements, warnings).
    // Older releases wrap it in `{ dataSet }`; a handful of forks use a
    // bare `loadedDataSets` map. Try all three so this keeps working
    // across version bumps.
    let ds = null;
    try {
        const mgr = wadouri;
        const byMgr = mgr.dataSetCacheManager?.get?.(url);
        if (byMgr) {
            // Most common: the returned object IS the dataSet.
            if (byMgr.elements) {
                ds = byMgr;
            }
            else if (byMgr.dataSet) {
                ds = byMgr.dataSet;
            }
        }
        if (!ds && mgr.loadedDataSets?.[url]?.dataSet) {
            ds = mgr.loadedDataSets[url].dataSet;
        }
    }
    catch {
        // fall through — callers treat null as "not loaded yet".
    }
    return ds?.elements ? ds : null;
}
/** The 1-based frame number a wadouri imageId addresses, or null for a single-frame id. */
function frameNumberOf(imageId) {
    const m = /[?&]frame=(\d+)/.exec(imageId);
    if (!m)
        return null;
    const n = Number.parseInt(m[1], 10);
    return Number.isFinite(n) && n >= 1 ? n : null;
}
/** Parse a multi-valued DS attribute into exactly `count` finite floats. */
function readFloats(ds, tag, count) {
    const raw = ds.string?.(tag);
    if (!raw)
        return undefined;
    const parts = raw.split('\\').map((v) => Number.parseFloat(v));
    if (parts.length < count || parts.slice(0, count).some((v) => !Number.isFinite(v))) {
        return undefined;
    }
    return parts.slice(0, count);
}
/** Keyed by the object's URL (frame suffix stripped); null = not a classic multi-frame volume. */
const classicMultiframeCache = new Map();
function objectUrlOf(imageId) {
    let url = imageId.slice('wadouri:'.length);
    const frameIndex = url.indexOf('frame=');
    if (frameIndex !== -1)
        url = url.substring(0, frameIndex - 1);
    return url;
}
function readClassicMultiframeGeometry(ds) {
    const frames = ds.intString?.('x00280008');
    if (!frames || frames < 2)
        return null;
    const elements = (ds.elements ?? {});
    // Enhanced multi-frame: the loader reads each frame's own plane position.
    if (elements.x52009230 || elements.x52009229)
        return null;
    const iop = readFloats(ds, 'x00200037', 6);
    const ipp = readFloats(ds, 'x00200032', 3);
    const pixelSpacing = readFloats(ds, 'x00280030', 2);
    if (!iop || !ipp || !pixelSpacing)
        return null;
    const spacingBetweenSlices = ds.floatString?.('x00180088');
    const sliceThickness = ds.floatString?.('x00180050');
    const step = typeof spacingBetweenSlices === 'number' &&
        Number.isFinite(spacingBetweenSlices) &&
        spacingBetweenSlices !== 0
        ? spacingBetweenSlices
        : typeof sliceThickness === 'number' && Number.isFinite(sliceThickness) && sliceThickness > 0
            ? sliceThickness
            : null;
    if (step === null)
        return null;
    const [rx, ry, rz, cx, cy, cz] = iop;
    const normal = [ry * cz - rz * cy, rz * cx - rx * cz, rx * cy - ry * cx];
    const len = Math.hypot(normal[0], normal[1], normal[2]);
    if (!(len > 0))
        return null;
    normal[0] /= len;
    normal[1] /= len;
    normal[2] /= len;
    // Same shape the loader's wadouri provider returns, so every consumer of the
    // module (volume builder, tools, orientation helpers) sees a complete answer.
    const base = {
        frameOfReferenceUID: ds.string?.('x00200052'),
        rows: ds.uint16?.('x00280010'),
        columns: ds.uint16?.('x00280011'),
        imageOrientationPatient: iop,
        rowCosines: [rx, ry, rz],
        columnCosines: [cx, cy, cz],
        sliceThickness: typeof sliceThickness === 'number' && Number.isFinite(sliceThickness)
            ? sliceThickness
            : undefined,
        // Unsigned: core only ever uses it as a fallback magnitude.
        spacingBetweenSlices: Math.abs(step),
        sliceLocation: undefined,
        pixelSpacing,
        rowPixelSpacing: pixelSpacing[0],
        columnPixelSpacing: pixelSpacing[1],
        usingDefaultValues: false,
    };
    return { origin: [ipp[0], ipp[1], ipp[2]], normal, step, base };
}
/**
 * The imagePlaneModule for one frame of a classic multi-frame volume, with
 * the frame's own position synthesised as origin + (frame − 1) × step × normal.
 * Undefined for everything else (single-frame ids, Enhanced objects, objects
 * without orientation/spacing, datasets not loaded yet), leaving the loader to
 * answer as before.
 */
function classicMultiframePlaneModule(imageId) {
    const frame = frameNumberOf(imageId);
    if (frame === null || !imageId.startsWith('wadouri:'))
        return undefined;
    const url = objectUrlOf(imageId);
    let geometry = classicMultiframeCache.get(url);
    if (geometry === undefined) {
        // Not loaded yet: answer nothing and do not cache, so a later lookup can succeed.
        const ds = getWadouriDataSet(imageId);
        if (!ds)
            return undefined;
        try {
            geometry = readClassicMultiframeGeometry(ds);
        }
        catch {
            geometry = null;
        }
        classicMultiframeCache.set(url, geometry);
    }
    if (!geometry)
        return undefined;
    const d = (frame - 1) * geometry.step;
    const { origin, normal } = geometry;
    return {
        ...geometry.base,
        imagePositionPatient: [
            origin[0] + d * normal[0],
            origin[1] + d * normal[1],
            origin[2] + d * normal[2],
        ],
    };
}
/**
 * The VOI LUT module, answered three ways:
 *  - an EMPTY module when the stored window must not be used (PET, or a
 *    full-range sentinel — see lib/cornerstone/autoWindow). An empty answer
 *    outranks the loader's, and the loader then windows from the decoded
 *    min/max; the stack viewport refines PET from percentiles after load.
 *  - the API's window when the header has not been read yet or agrees with it.
 *  - undefined otherwise, so the header answers.
 */
function voiLutModuleFor(imageId, cached) {
    const ds = getWadouriDataSet(imageId);
    const modality = ds?.string?.('x00080060') ?? null;
    const windowCenter = cached?.windowCenter ?? ds?.floatString?.('x00281050') ?? null;
    const windowWidth = cached?.windowWidth ?? ds?.floatString?.('x00281051') ?? null;
    const bitsStored = cached?.bitsStored ?? ds?.uint16?.('x00280101') ?? null;
    const rescaleSlope = cached?.rescaleSlope ?? ds?.floatString?.('x00281053') ?? null;
    const hasStoredWindow = windowCenter != null && windowWidth != null;
    if (isDataWindowedModality(modality) ||
        (hasStoredWindow &&
            !isUsableStoredWindow({ modality, windowCenter, windowWidth, bitsStored, rescaleSlope }))) {
        return {};
    }
    if (cached?.windowCenter == null || cached?.windowWidth == null)
        return undefined;
    return {
        windowCenter: [cached.windowCenter],
        windowWidth: [cached.windowWidth],
    };
}
function readUltrasoundRegionsFromDataset(imageId) {
    if (ultrasoundRegionsCache.has(imageId)) {
        return ultrasoundRegionsCache.get(imageId) ?? null;
    }
    const ds = getWadouriDataSet(imageId);
    if (!ds?.elements)
        return null;
    const seq = ds.elements.x00186011;
    if (!seq?.items || seq.items.length === 0) {
        ultrasoundRegionsCache.set(imageId, null);
        return null;
    }
    const regions = [];
    for (const item of seq.items) {
        const d = item.dataSet;
        if (!d)
            continue;
        regions.push({
            RegionLocationMinX0: safeUint32(d, 'x00186018') ?? 0,
            RegionLocationMinY0: safeUint32(d, 'x0018601a') ?? 0,
            RegionLocationMaxX1: safeUint32(d, 'x0018601c') ?? 0,
            RegionLocationMaxY1: safeUint32(d, 'x0018601e') ?? 0,
            RegionSpatialFormat: safeUint16(d, 'x00186012'),
            RegionDataType: safeUint16(d, 'x00186014'),
            PhysicalUnitsXDirection: safeUint16(d, 'x00186024'),
            PhysicalUnitsYDirection: safeUint16(d, 'x00186026'),
            PhysicalDeltaX: safeDouble(d, 'x0018602c'),
            PhysicalDeltaY: safeDouble(d, 'x0018602e'),
            // VR SL — a 0 offset means "baseline at the region edge", so no
            // `|| null` coercion (the loader's built-in module gets this wrong).
            ReferencePixelX0: safeInt32(d, 'x00186020'),
            ReferencePixelY0: safeInt32(d, 'x00186022'),
            // VR FD — 8-byte double, NOT uint16.
            RefPixelPhysicalValueX: safeDouble(d, 'x00186028'),
            RefPixelPhysicalValueY: safeDouble(d, 'x0018602a'),
        });
    }
    ultrasoundRegionsCache.set(imageId, regions);
    return regions;
}
function safeUint32(d, tag) {
    try {
        const v = d.uint32?.(tag);
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
    catch {
        return null;
    }
}
function safeUint16(d, tag) {
    try {
        const v = d.uint16?.(tag);
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
    catch {
        return null;
    }
}
function safeInt32(d, tag) {
    try {
        const v = d.int32?.(tag);
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
    catch {
        return null;
    }
}
function safeDouble(d, tag) {
    try {
        const v = d.double?.(tag);
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
    catch {
        return null;
    }
}
/**
 * Calibration payload for projection radiographs (DX/CR) whose only spacing
 * tag is Imager Pixel Spacing (0018,1164) — the normal case for veterinary
 * flat-panel and CR units. The wadouri loader's imagePlaneModule reads only
 * Pixel Spacing (0028,0030), so without this provider such images measure in
 * px. Served for the 'calibratedPixelSpacing' module, which
 * StackViewport.calibrateIfNecessary consults per stack image. Returning
 * undefined leaves an image exactly as before — CT/MR/US and
 * PixelSpacing-carrying files are untouched, and this never populates
 * imagePlaneModule, so the MPR volume builder is unaffected.
 *
 * The spacing runs through core's getPixelSpacingInformation (PS3.3 10.7):
 * plain ImagerPixelSpacing → type 'Proj' (detector-plane measurement, shown
 * as "mm Proj"); with ERMF or a derivable SID/SOD ratio → magnification-
 * corrected spacing, type 'ERMF'.
 */
const projectionCalibrationCache = new Map();
/** Parse a multi-valued DS attribute ("0.125\\0.125") into positive floats. */
function readPositiveFloats(ds, tag) {
    const raw = ds.string?.(tag);
    if (!raw)
        return undefined;
    const parts = raw.split('\\').map((v) => Number.parseFloat(v));
    if (parts.length === 0 || parts.some((v) => !Number.isFinite(v) || v <= 0))
        return undefined;
    return parts;
}
function readProjectionCalibrationFromDataset(imageId) {
    if (projectionCalibrationCache.has(imageId)) {
        return projectionCalibrationCache.get(imageId) ?? undefined;
    }
    // Not loaded yet: return without caching so a later lookup can succeed.
    const ds = getWadouriDataSet(imageId);
    if (!ds)
        return undefined;
    let result = null;
    try {
        const pixelSpacing = readPositiveFloats(ds, 'x00280030');
        const imagerPixelSpacing = readPositiveFloats(ds, 'x00181164');
        // Only fill the gap the loader can't: ImagerPixelSpacing present with no
        // PixelSpacing. When 0028,0030 exists the loader already yields mm and
        // relabeling working images (e.g. "mm Unknown") would be a regression.
        if (!pixelSpacing && imagerPixelSpacing && imagerPixelSpacing.length >= 2) {
            const info = csCoreUtils.getPixelSpacingInformation({
                SOPClassUID: ds.string?.('x00080016'),
                PixelSpacing: undefined,
                ImagerPixelSpacing: imagerPixelSpacing,
                EstimatedRadiographicMagnificationFactor: ds.floatString?.('x00181114'),
                DistanceSourceToDetector: ds.floatString?.('x00181110'),
                DistanceSourceToPatient: ds.floatString?.('x00181111'),
                PixelSpacingCalibrationType: ds.string?.('x00280a02'),
            });
            const spacing = Array.isArray(info?.PixelSpacing) ? info.PixelSpacing : null;
            const row = Number(spacing?.[0]);
            const col = Number(spacing?.[1]);
            if (info?.isProjection && row > 0 && col > 0) {
                // Deliberately no `scale`: the tools divide by row/columnPixelSpacing
                // (getCalibratedLengthUnitsAndScale), so a scale would double-apply.
                result = { rowPixelSpacing: row, columnPixelSpacing: col, type: info.type ?? '' };
            }
        }
    }
    catch {
        result = null;
    }
    projectionCalibrationCache.set(imageId, result);
    return result ?? undefined;
}
/**
 * Bulk-register metadata for a series of images. Caches every imageId
 * whose URL contains the image's storageLocation — multi-frame images
 * expand into one imageId per frame (`...?frame=N`), and the previous
 * `.find()` only matched the first frame, leaving subsequent frames
 * uncached and falling back to the slow per-frame provider lookup.
 */
export function cacheSeriesMetadata(imageIds, images) {
    for (const image of images) {
        if (!image.displayMetadata)
            continue;
        // An imageId embeds whichever URL lib/localGateway made this image's source —
        // the LAN one for a clinic that serves DICOM locally. Matching on the cloud
        // URL alone would miss every id for those clinics, silently dropping the
        // API's window/level and pixel metadata for the whole study.
        const sources = [image.storageLocation, image.localStorageLocation].filter((s) => Boolean(s));
        for (const imageId of imageIds) {
            // react-doctor-disable-next-line react-doctor/js-set-map-lookups -- String.includes (substring/frame-URL match), not array membership
            if (sources.some((source) => imageId.includes(source))) {
                metadataCache.set(imageId, image.displayMetadata);
            }
        }
    }
}
/**
 * The pixel module from the API's displayMetadata, answered ONLY while the file
 * itself cannot answer.
 *
 * This provider is registered at priority 100 and the loader's wadouri provider
 * at 0, and core asks providers highest first — so whatever this returns is what
 * the loader DECODES with (getImageFrame reads 'imagePixelModule' through
 * metaData.get). That is wrong in two ways:
 *
 *  - A row with no derived columns (bytes copied without being read, as the
 *    Keystone migration once did) answered with every field undefined, which
 *    overrode a header that knew the real values.
 *  - Even a complete row has no samplesPerPixel, planarConfiguration or palette
 *    lookup tables — the database does not store them — so a planar RGB or
 *    PALETTE COLOR image decoded with those fields missing.
 *
 * So once the object's dataset is loaded (always the case by the time wadouri
 * decodes it) this returns undefined and the header answers everything. Before
 * that, callers only size things (the MPR memory estimate reads rows/columns),
 * and the database answer is given only when it is complete, so a partial or
 * empty row is never mistaken for the truth.
 */
function databasePixelModule(imageId, cached) {
    if (getWadouriDataSet(imageId))
        return undefined;
    const { bitsAllocated, bitsStored, pixelRepresentation, rows, columns, photometricInterpretation, } = cached;
    if (bitsAllocated == null ||
        bitsStored == null ||
        pixelRepresentation == null ||
        rows == null ||
        columns == null ||
        !photometricInterpretation) {
        return undefined;
    }
    return {
        bitsAllocated,
        bitsStored,
        pixelRepresentation,
        rows,
        columns,
        photometricInterpretation,
    };
}
/**
 * Custom metadata provider for Cornerstone.
 * Returns calibration data from our API when the DICOM loader didn't extract it.
 *
 * Registered at priority 100, which is HIGHER than the DICOM image loader's own
 * providers (registered at the default 0): core asks providers in descending
 * priority and takes the first defined answer. So anything returned here
 * overrides the file header — every module below must return undefined unless
 * it is filling a gap the loader cannot, or is certain to agree with the file.
 */
function customMetadataProvider(type, imageId) {
    // Ultrasound regions: always try to read from the DICOM dataset. This
    // is independent of the API-provided displayMetadata cache — it runs
    // for every wadouri-loaded image regardless. Echo calibration depends
    // on this.
    if (type === 'ultrasoundRegionsModule' || type === 'USRegions') {
        const regions = readUltrasoundRegionsFromDataset(imageId);
        if (!regions || regions.length === 0)
            return undefined;
        return { SequenceOfUltrasoundRegions: regions, regions };
    }
    // Deliberately NOT answered: `instance`. This provider outranks the loader's
    // (100 vs 0), so any answer here would REPLACE the loader's whole instance
    // object — patient, study and SUV fields included (dicomMetadata,
    // suvCalculations, fusionDetection and core's splitImageIdsBy4DTags read it)
    // — with one carrying only the regions. Every regions reader asks
    // 'ultrasoundRegionsModule' above first, so the instance copy was never needed.
    // Projection-radiograph calibration: DX/CR files that carry only
    // ImagerPixelSpacing (0018,1164) get their mm spacing from here — nothing
    // else in the stack answers this module (verified against loader 4.22.13).
    if (type === 'calibratedPixelSpacing') {
        return readProjectionCalibrationFromDataset(imageId);
    }
    // imagePlaneModule is answered for exactly one shape: a frame of a classic
    // multi-frame volume, where it is COMPLETE (every field the loader returns)
    // with the frame's own position. It is never built from the API's
    // displayMetadata — a partial module (pixelSpacing without orientation)
    // breaks Cornerstone's volume builder, which assumes all fields are present.
    // DX/CR files that only carry ImagerPixelSpacing are calibrated through the
    // 'calibratedPixelSpacing' module above, a display-level path that never
    // feeds the volume builder.
    if (type === 'imagePlaneModule') {
        return classicMultiframePlaneModule(imageId);
    }
    const cached = metadataCache.get(imageId);
    // Answered with or without the API's row: a PET file with no displayMetadata
    // still must not be windowed by its header.
    if (type === 'voiLutModule') {
        return voiLutModuleFor(imageId, cached);
    }
    if (!cached)
        return undefined;
    if (type === 'imagePixelModule') {
        return databasePixelModule(imageId, cached);
    }
    return undefined;
}
let providerRegistered = false;
/**
 * Register the custom metadata provider with Cornerstone.
 * Call once after Cornerstone init.
 */
export function registerMetadataProvider() {
    if (providerRegistered)
        return;
    // Priority 100 is ABOVE the DICOM image loader's providers, which register at the
    // default priority 0 (@cornerstonejs/dicom-image-loader 4.22.13, wadouri/register.js),
    // and core returns the first defined answer in descending priority. So this
    // provider WINS wherever it answers; see customMetadataProvider for why each
    // module is careful to return undefined when the file should answer instead.
    metaData.addProvider(customMetadataProvider, 100);
    providerRegistered = true;
    console.log('[Cornerstone] Custom metadata provider registered');
}
